/**
 * ReaderListPage - Main reader/narrator management page with list and search
 *
 * Displays readers with search functionality and pagination
 */
package com.castly.admin.ui.readers

import androidx.compose.runtime.*
import com.castly.admin.api.ApiClient
import com.castly.admin.ui.theme.AppTheme
import com.castly.admin.ui.theme.BorderRadius
import com.castly.admin.ui.theme.LightColors
import com.castly.admin.ui.theme.Spacing
import com.castly.admin.ui.theme.Typography
import com.castly.admin.ui.theme.themeTransition
import com.castly.shared.dto.ReaderDto
import com.castly.shared.dto.CreateReaderRequest
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*

@Composable
fun ReaderListPage(
    apiClient: ApiClient,
    onReaderClick: (Int) -> Unit = {}
) {
    val colors = AppTheme.colors
    val userRoleStr = window.localStorage.getItem("user_role") ?: "NONE"
    val canEdit = userRoleStr.uppercase() in setOf("LABEL_EDITOR", "EDITOR", "ADMIN", "SUPER_ADMIN")

    var readers by remember { mutableStateOf<List<ReaderDto>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var totalCount by remember { mutableStateOf(0) }
    var currentPage by remember { mutableStateOf(1) }
    var searchQuery by remember { mutableStateOf("") }
    var showCreateDialog by remember { mutableStateOf(false) }
    var isCreating by remember { mutableStateOf(false) }
    var createError by remember { mutableStateOf<String?>(null) }
    var expandedReaderId by remember { mutableStateOf<Int?>(null) }
    var expandedAudiobooks by remember { mutableStateOf<List<com.castly.shared.dto.AudiobookDto>>(emptyList()) }
    var expandedLoading by remember { mutableStateOf(false) }

    val pageSize = 20
    val scope = rememberCoroutineScope()

    fun loadReaders() {
        scope.launch {
            loading = true
            error = null
            try {
                val response = apiClient.listReaders(
                    page = currentPage,
                    pageSize = pageSize,
                    query = searchQuery.takeIf { it.isNotBlank() }
                )
                readers = response.readers
                totalCount = response.total
            } catch (e: Exception) {
                error = e.message ?: "Failed to load readers"
            } finally {
                loading = false
            }
        }
    }

    LaunchedEffect(currentPage, searchQuery) {
        loadReaders()
    }

    Div({
        style {
            height(100.percent)
            display(DisplayStyle.Flex)
            flexDirection(FlexDirection.Column)
            backgroundColor(Color(colors.surfaceHover))
            themeTransition()
        }
    }) {
        // Header with search and create button
        Div({
            style {
                padding(Spacing.lg)
                backgroundColor(Color(colors.surface))
                property("border-bottom", "1px solid ${colors.border}")
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                gap(Spacing.lg)
                property("flex-shrink", "0")
                themeTransition()
            }
        }) {
            // Search bar
            Div({
                style {
                    flex("1")
                    maxWidth(400.px)
                }
            }) {
                Input(InputType.Text) {
                    value(searchQuery)
                    onInput { searchQuery = it.value; currentPage = 1 }
                    attr("placeholder", "Search readers/narrators...")
                    style {
                        width(100.percent)
                        padding(10.px, Spacing.lg)
                        fontSize(Typography.base)
                        border(1.px, LineStyle.Solid, Color(colors.border))
                        borderRadius(BorderRadius.md)
                        property("outline", "none")
                        property("box-sizing", "border-box")
                    }
                }
            }

            // Create button
            if (canEdit) {
                com.castly.admin.ui.components.Tooltip(text = "Create Reader") {
                    Button({
                        onClick { showCreateDialog = true; createError = null }
                        attr("aria-label", "Create Reader")
                        style {
                            padding(Spacing.sm)
                            backgroundColor(Color(colors.primary))
                            color(Color("#ffffff"))
                            border(0.px)
                            borderRadius(BorderRadius.md)
                            cursor("pointer")
                            display(DisplayStyle.Flex)
                            alignItems(AlignItems.Center)
                            justifyContent(JustifyContent.Center)
                            width(36.px)
                            height(36.px)
                        }
                    }) {
                        com.castly.admin.ui.icons.IconPlus(size = 18)
                    }
                }
            }
        }

        // Main content area
        Div({
            style {
                flex("1")
                overflow("auto")
                padding(Spacing.lg)
            }
        }) {
            when {
                loading -> {
                    Div({
                        style {
                            textAlign("center")
                            padding(32.px)
                            color(Color(colors.textMuted))
                        }
                    }) {
                        Text("Loading readers...")
                    }
                }

                error != null -> {
                    Div({
                        style {
                            backgroundColor(Color(if (colors == LightColors) "#fee2e2" else "#450a0a"))
                            border(1.px, LineStyle.Solid, Color(if (colors == LightColors) "#fca5a5" else "#991b1b"))
                            borderRadius(BorderRadius.md)
                            padding(Spacing.lg)
                            themeTransition()
                        }
                    }) {
                        Text("Error: $error")
                    }
                }

                readers.isEmpty() -> {
                    Div({
                        style {
                            textAlign("center")
                            padding(48.px, Spacing.xxl)
                            backgroundColor(Color(colors.surface))
                            borderRadius(BorderRadius.lg)
                            border(1.px, LineStyle.Solid, Color(colors.border))
                            themeTransition()
                        }
                    }) {
                        H3({
                            style {
                                margin(0.px)
                                marginBottom(Spacing.sm)
                                fontSize(Typography.xl)
                                fontWeight("600")
                                color(Color(colors.text))
                            }
                        }) {
                            Text(if (searchQuery.isNotBlank()) "No readers found" else "No readers yet")
                        }
                        P({
                            style {
                                margin(0.px)
                                fontSize(Typography.base)
                                color(Color(colors.textMuted))
                            }
                        }) {
                            Text(if (searchQuery.isNotBlank()) "Try adjusting your search query" else "Create your first reader to get started")
                        }
                    }
                }

                else -> {
                    // Readers list
                    Div({
                        style {
                            backgroundColor(Color(colors.surface))
                            borderRadius(BorderRadius.lg)
                            border(1.px, LineStyle.Solid, Color(colors.border))
                            overflow("hidden")
                            themeTransition()
                        }
                    }) {
                        readers.forEachIndexed { index, reader ->
                            val isExpanded = expandedReaderId == reader.id
                            Div({
                                style {
                                    if (index > 0) property("border-top", "1px solid ${colors.border}")
                                }
                            }) {
                                // Reader row (clickable to expand)
                                Div({
                                    onClick {
                                        if (expandedReaderId == reader.id) {
                                            expandedReaderId = null
                                        } else {
                                            expandedReaderId = reader.id
                                            expandedLoading = true
                                            scope.launch {
                                                try {
                                                    expandedAudiobooks = apiClient.listAudiobooks(readerId = reader.id).audiobooks
                                                } catch (_: Exception) {
                                                    expandedAudiobooks = emptyList()
                                                } finally {
                                                    expandedLoading = false
                                                }
                                            }
                                        }
                                    }
                                    style {
                                        padding(Spacing.lg)
                                        cursor("pointer")
                                        property("transition", "background-color 0.2s")
                                        if (isExpanded) backgroundColor(Color(colors.surfaceHover))
                                    }
                                    onMouseOver { it.target.asDynamic().style.backgroundColor = colors.surfaceHover }
                                    onMouseOut { if (!isExpanded) it.target.asDynamic().style.backgroundColor = "transparent" }
                                }) {
                                    Div({
                                        style {
                                            display(DisplayStyle.Flex)
                                            alignItems(AlignItems.FlexStart)
                                            gap(Spacing.sm)
                                        }
                                    }) {
                                        Span({ style { fontSize(Typography.base); color(Color(colors.textMuted)); marginTop(4.px) } }) {
                                            Text(if (isExpanded) "▼" else "▶")
                                        }
                                        Div({
                                            style { flex("1") }
                                        }) {
                                            // Name
                                            Div({
                                                style {
                                                    fontSize(Typography.lg)
                                                    fontWeight("600")
                                                    color(Color(colors.text))
                                                    marginBottom(Spacing.xs)
                                                }
                                            }) {
                                                Text(reader.name)
                                            }

                                            // Biography preview
                                            reader.biography?.let { bio ->
                                                Div({
                                                    style {
                                                        fontSize(Typography.base)
                                                        color(Color(colors.textMuted))
                                                        property("overflow", "hidden")
                                                        property("text-overflow", "ellipsis")
                                                        property("white-space", "nowrap")
                                                        maxWidth(600.px)
                                                    }
                                                }) {
                                                    Text(bio)
                                                }
                                            }

                                            // Audiobook count
                                            reader.audiobookCount?.let { count ->
                                                Div({
                                                    style {
                                                        fontSize(13.px)
                                                        color(Color(colors.textMuted))
                                                        marginTop(Spacing.xs)
                                                    }
                                                }) {
                                                    Text("$count audiobook${if (count != 1) "s" else ""}")
                                                }
                                            }
                                        }
                                    }
                                }

                                // Expanded audiobooks panel
                                if (isExpanded) {
                                    Div({
                                        style {
                                            padding(0.px, Spacing.lg, Spacing.lg, Spacing.lg)
                                            backgroundColor(Color(colors.surfaceHover))
                                            maxHeight(300.px)
                                            overflow("auto")
                                        }
                                    }) {
                                        if (expandedLoading) {
                                            Div({ style { padding(Spacing.lg); color(Color(colors.textMuted)); textAlign("center") } }) {
                                                Text("Loading audiobooks...")
                                            }
                                        } else if (expandedAudiobooks.isEmpty()) {
                                            Div({ style { padding(Spacing.lg); color(Color(colors.textMuted)); textAlign("center") } }) {
                                                Text("No audiobooks found for this reader")
                                            }
                                        } else {
                                            expandedAudiobooks.forEach { audiobook ->
                                                Div({
                                                    onClick { it.stopPropagation() }
                                                    style {
                                                        padding(Spacing.sm, Spacing.md)
                                                        backgroundColor(Color(colors.surface))
                                                        borderRadius(BorderRadius.sm)
                                                        marginBottom(Spacing.xs)
                                                        cursor("pointer")
                                                        display(DisplayStyle.Flex)
                                                        alignItems(AlignItems.Center)
                                                        gap(Spacing.sm)
                                                    }
                                                    onMouseOver { it.target.asDynamic().style.opacity = "0.8" }
                                                    onMouseOut { it.target.asDynamic().style.opacity = "1" }
                                                }) {
                                                    Span({ style { fontSize(Typography.xs); padding(2.px, 6.px); backgroundColor(Color("#4CAF50")); color(Color("#fff")); borderRadius(BorderRadius.sm) } }) { Text("Audiobook") }
                                                    Span({ style { fontWeight("500"); color(Color(colors.text)) } }) { Text(audiobook.title) }
                                                }
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }

                    // Pagination
                    if (totalCount > pageSize) {
                        Div({
                            style {
                                display(DisplayStyle.Flex)
                                justifyContent(JustifyContent.Center)
                                alignItems(AlignItems.Center)
                                gap(Spacing.lg)
                                marginTop(Spacing.lg)
                                padding(Spacing.lg)
                            }
                        }) {
                            val totalPages = (totalCount + pageSize - 1) / pageSize

                            Button({
                                onClick { if (currentPage > 1) currentPage-- }
                                if (currentPage <= 1) disabled()
                                style {
                                    padding(Spacing.sm, Spacing.lg)
                                    fontSize(Typography.base)
                                    fontWeight("500")
                                    color(if (currentPage <= 1) Color(colors.textMuted) else Color(colors.text))
                                    backgroundColor(Color(colors.surface))
                                    border(1.px, LineStyle.Solid, Color(colors.border))
                                    borderRadius(BorderRadius.md)
                                    cursor(if (currentPage <= 1) "not-allowed" else "pointer")
                                }
                            }) {
                                Text("Previous")
                            }

                            Span({
                                style {
                                    fontSize(Typography.base)
                                    color(Color(colors.text))
                                }
                            }) {
                                Text("Page $currentPage of $totalPages")
                            }

                            Button({
                                onClick { if (currentPage < totalPages) currentPage++ }
                                if (currentPage >= totalPages) disabled()
                                style {
                                    padding(Spacing.sm, Spacing.lg)
                                    fontSize(Typography.base)
                                    fontWeight("500")
                                    color(if (currentPage >= totalPages) Color(colors.textMuted) else Color(colors.text))
                                    backgroundColor(Color(colors.surface))
                                    border(1.px, LineStyle.Solid, Color(colors.border))
                                    borderRadius(BorderRadius.md)
                                    cursor(if (currentPage >= totalPages) "not-allowed" else "pointer")
                                }
                            }) {
                                Text("Next")
                            }
                        }
                    }
                }
            }
        }
    }

    // Create reader dialog (modal overlay)
    if (showCreateDialog) {
        Div({
            style {
                position(Position.Fixed)
                top(0.px)
                left(0.px)
                right(0.px)
                bottom(0.px)
                backgroundColor(Color("rgba(0, 0, 0, 0.5)"))
                display(DisplayStyle.Flex)
                alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center)
                property("z-index", "1000")
            }
            onClick { if (!isCreating) { showCreateDialog = false; createError = null } }
        }) {
            Div({
                style {
                    maxWidth(500.px)
                    width(90.percent)
                }
                onClick { it.stopPropagation() }
            }) {
                ReaderForm(
                    reader = null,
                    isLoading = isCreating,
                    errorMessage = createError,
                    onSave = { name, biography ->
                        scope.launch {
                            isCreating = true
                            createError = null
                            try {
                                apiClient.createReader(CreateReaderRequest(name, biography))
                                showCreateDialog = false
                                loadReaders()
                            } catch (e: Exception) {
                                createError = e.message ?: "Failed to create reader"
                            } finally {
                                isCreating = false
                            }
                        }
                    },
                    onCancel = {
                        if (!isCreating) {
                            showCreateDialog = false
                            createError = null
                        }
                    }
                )
            }
        }
    }
}
